// Non-destructive image adjustments. LUT-based tone ops (exposure/contrast/levels/
// lightness/curves) are applied via an SVG feComponentTransfer (GPU, real-time);
// hue/saturation use CSS filter functions.

export const DEFAULT_ADJUSTMENTS = {
  exposure: 0,      // -3 .. +3
  contrast: 0,      // -100 .. +100
  hue: 0,           // -180 .. +180
  saturation: 0,    // -100 .. +100
  lightness: 0,     // -100 .. +100
  levelBlack: 0,    // 0 .. 255
  levelWhite: 255,  // 0 .. 255
  gamma: 1.0,       // 0.1 .. 9.9
  curve: [[0, 0], [255, 255]],
};

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

// Catmull-Rom interpolation through control points -> 256-value LUT.
export function curveToLUT(points) {
  const p = [...(points || [[0, 0], [255, 255]])].sort((a, b) => a[0] - b[0]);
  const lut = new Array(256);
  for (let i = 0; i < 256; i++) {
    if (i <= p[0][0]) { lut[i] = p[0][1]; continue; }
    if (i >= p[p.length - 1][0]) { lut[i] = p[p.length - 1][1]; continue; }
    let k = 0;
    while (k < p.length - 1 && p[k + 1][0] < i) k++;
    const p1 = p[k], p2 = p[k + 1], p0 = p[k - 1] || p1, p3 = p[k + 2] || p2;
    const t = (i - p1[0]) / ((p2[0] - p1[0]) || 1);
    const t2 = t * t, t3 = t2 * t;
    const y = 0.5 * ((2 * p1[1]) + (-p0[1] + p2[1]) * t +
      (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * t2 +
      (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * t3);
    lut[i] = clamp(Math.round(y), 0, 255);
  }
  return lut;
}

function curveIsIdentity(c) {
  return !c || (c.length === 2 && c[0][0] === 0 && c[0][1] === 0 && c[1][0] === 255 && c[1][1] === 255);
}

function needsLUT(a) {
  return (a.levelBlack || 0) > 0 || (a.levelWhite ?? 255) < 255 ||
    (a.gamma && a.gamma !== 1) || !curveIsIdentity(a.curve);
}

export function hasActiveAdjustments(a) {
  if (!a) return false;
  return !!(a.exposure || a.contrast || a.hue || a.saturation || a.lightness || needsLUT(a));
}

// Levels + Curves only (these need a per-channel LUT). Other tone ops use CSS functions.
export function buildTableValues(a) {
  const curveLut = curveToLUT(a.curve);
  const black = a.levelBlack || 0;
  const white = a.levelWhite ?? 255;
  const gamma = a.gamma || 1;
  const denom = Math.max(1, white - black);
  const out = new Array(256);
  for (let i = 0; i < 256; i++) {
    let nv = clamp((i - black) / denom, 0, 1);
    let v = Math.pow(nv, 1 / gamma) * 255;      // levels
    v = curveLut[clamp(Math.round(v), 0, 255)]; // curves
    out[i] = (clamp(v, 0, 255) / 255).toFixed(4);
  }
  return out.join(" ");
}

// Exposure/Contrast/Lightness/Saturation/Hue -> universally-supported CSS filter
// functions; Levels/Curves -> SVG feComponentTransfer LUT (url reference).
export function buildAdjustments(a) {
  if (!a) return { css: "", tableValues: null };
  const css = [];
  if (a.exposure) css.push(`brightness(${Math.pow(2, a.exposure).toFixed(4)})`);     // value * 2^exposure
  if (a.contrast) css.push(`contrast(${clamp(1 + a.contrast / 100, 0, 4).toFixed(3)})`); // pivots on mid-gray
  if (a.lightness) css.push(`brightness(${clamp(1 + a.lightness / 100, 0, 4).toFixed(3)})`);
  if (a.saturation) css.push(`saturate(${clamp(1 + a.saturation / 100, 0, 4).toFixed(3)})`);
  if (a.hue) css.push(`hue-rotate(${a.hue}deg)`);
  const tableValues = needsLUT(a) ? buildTableValues(a) : null;
  return { css: css.join(" "), tableValues };
}
